import { useMemo, useRef, useState } from "react";
import { Box, DoorOpen, Layers, Library } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { KorpusBuilderV2 } from "@/components/komponent/KorpusBuilderV2";
import { ZasuvkaDialog } from "@/components/komponent/ZasuvkaDialog";
import { DvereDialog } from "@/components/komponent/DvereDialog";
import {
  useKomponentKusovnik,
  useKomponenty,
  useVlozKomponent,
} from "@/api/hooks/useKomponenty";
import { useCreateZostava, useDeleteZostava } from "@/api/hooks/useZostavy";
import { konfigZKomponentu } from "@/lib/korpus/fromKomponent";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import type {
  KomponentKusovnik,
  KomponentMeta,
  KomponentParamDef,
  MaterialSlot,
  Zostava,
} from "@/api/schemas";
import { suggestNextZostavaKod } from "@/lib/dielecKod";

/** Návrh kódu novej zostavy — písmeno za existujúcimi (KOLO 134).
 *  Do KOLA 134 `K1`, `K2`…; kód aj tak pridelí backend podľa poradia. */
export function navrhniKod(zostavy: Zostava[]): string {
  return suggestNextZostavaKod(zostavy);
}

type Rezim =
  | { view: "picker" }
  | { view: "builder"; zostava: Zostava; konfig: Record<string, unknown> | null }
  | { view: "form"; komponent: KomponentMeta }
  | { view: "dvere" }
  | { view: "zasuvka" };

export interface KomponentDialogProps {
  zakazkaId: string;
  variantaId: string;
  zostavy: Zostava[];
  sloty: MaterialSlot[];
  /** Zostava vybraná v editore — default pre Dvere/Zásuvku. */
  selectedZostavaId?: string | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

/**
 * Jediný vstup pre komponenty — „Komponent":
 *
 * - korpusy (naučené z knižnice aj prázdny) → NOVÁ zostava + Korpus
 *   Builder (prednastavený podľa komponentu, 2D náhľad, dielenské
 *   pravidlá),
 * - komponent bez builder bloku → formulár + dry-run kusovník,
 * - dvere / zásuvka → vloženie do EXISTUJÚCEJ zostavy (s kovaním).
 */
export function KomponentDialog({
  zakazkaId,
  variantaId,
  zostavy,
  sloty,
  selectedZostavaId,
  open,
  onOpenChange,
}: KomponentDialogProps): JSX.Element {
  const [rezim, setRezim] = useState<Rezim>({ view: "picker" });
  const builderVlozene = useRef(false);
  const komponentyQuery = useKomponenty();
  const createZostava = useCreateZostava(zakazkaId, variantaId);
  const deleteZostava = useDeleteZostava(zakazkaId);

  const komponenty = useMemo(
    () => komponentyQuery.data?.data ?? [],
    [komponentyQuery.data],
  );

  function zavri(): void {
    setRezim({ view: "picker" });
    onOpenChange(false);
  }

  /** Nová zostava + builder (prázdny alebo prednastavený komponentom). */
  async function otvorBuilder(komponent: KomponentMeta | null): Promise<void> {
    const konfig = komponent ? konfigZKomponentu(komponent) : null;
    try {
      const res = await createZostava.mutateAsync({
        kod: navrhniKod(zostavy),
        nazov: komponent?.label ?? "Korpus",
        pocet_ks: 1,
        poradie: zostavy.length,
      });
      builderVlozene.current = false;
      setRezim({ view: "builder", zostava: res.data, konfig });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba vytvorenia zostavy",
        description: extractErrorMessage(err),
      });
    }
  }

  function handleKomponent(k: KomponentMeta): void {
    if (konfigZKomponentu(k)) {
      void otvorBuilder(k);
    } else {
      setRezim({ view: "form", komponent: k });
    }
  }

  // ── režimy mimo pickera ──
  if (rezim.view === "builder") {
    return (
      <KorpusBuilderV2
        zakazkaId={zakazkaId}
        zostavaId={rezim.zostava.id}
        sloty={sloty}
        open
        initialKonfig={rezim.konfig}
        onCreated={() => {
          builderVlozene.current = true;
        }}
        onOpenChange={(o) => {
          if (o) return;
          if (!builderVlozene.current) {
            // zrušené bez vloženia — prázdnu zostavu upraceme
            deleteZostava.mutate(rezim.zostava.id);
            setRezim({ view: "picker" });
          } else {
            zavri();
          }
        }}
      />
    );
  }
  if (rezim.view === "form") {
    return (
      <KomponentFormDialog
        zakazkaId={zakazkaId}
        variantaId={variantaId}
        zostavy={zostavy}
        sloty={sloty}
        komponent={rezim.komponent}
        onBack={() => setRezim({ view: "picker" })}
        onClose={zavri}
      />
    );
  }
  if (rezim.view === "dvere") {
    return (
      <DvereDialog
        zakazkaId={zakazkaId}
        zostavy={zostavy}
        selectedZostavaId={selectedZostavaId}
        sloty={sloty}
        open={open}
        onOpenChange={(o) => (o ? undefined : zavri())}
      />
    );
  }
  if (rezim.view === "zasuvka") {
    return (
      <ZasuvkaDialog
        zakazkaId={zakazkaId}
        zostavy={zostavy}
        selectedZostavaId={selectedZostavaId}
        sloty={sloty}
        open={open}
        onOpenChange={(o) => (o ? undefined : zavri())}
      />
    );
  }

  // ── picker ──
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? undefined : zavri())}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Vložiť komponent</DialogTitle>
          <DialogDescription className="sr-only">
            Výber komponentu z knižnice alebo prázdneho korpusu.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Korpus — otvorí sa builder (nová zostava)
            </p>
            <div className="grid grid-cols-2 gap-2">
              <KartaButton
                icon={<Box className="h-6 w-6 text-primary" />}
                label="Prázdny korpus"
                popis="builder od nuly — otvory, police, dvierka, zásuvky"
                onClick={() => void otvorBuilder(null)}
                disabled={createZostava.isPending}
              />
              {komponenty.map((k) => (
                <KartaButton
                  key={k.id}
                  icon={<Library className="h-6 w-6 text-primary" />}
                  label={k.label}
                  popis={
                    k.builder
                      ? "naučené z TopSolidu — otvorí sa v builderi"
                      : (k.popis ?? "").slice(0, 80) || "formulár s kusovníkom"
                  }
                  onClick={() => handleKomponent(k)}
                  disabled={createZostava.isPending}
                />
              ))}
            </div>
            {komponentyQuery.isLoading ? (
              <p className="mt-1 text-xs text-muted-foreground">Načítavam knižnicu…</p>
            ) : null}
          </div>
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Do existujúcej zostavy
            </p>
            <div className="grid grid-cols-2 gap-2">
              <KartaButton
                icon={<DoorOpen className="h-6 w-6 text-primary" />}
                label="Dvere"
                popis="samostatné dvere + závesy a podložky podľa dielne"
                onClick={() => setRezim({ view: "dvere" })}
                disabled={zostavy.length === 0}
              />
              <KartaButton
                icon={<Layers className="h-6 w-6 text-primary" />}
                label="Zásuvka"
                popis="čelo + box + výsuvy podľa hĺbky korpusu"
                onClick={() => setRezim({ view: "zasuvka" })}
                disabled={zostavy.length === 0}
              />
            </div>
            {zostavy.length === 0 ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Dvere a zásuvka potrebujú existujúcu zostavu — najprv vytvor korpus.
              </p>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KartaButton({
  icon,
  label,
  popis,
  onClick,
  disabled,
}: {
  icon: JSX.Element;
  label: string;
  popis: string;
  onClick: () => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex items-start gap-3 rounded-lg border p-3 text-left transition-colors hover:border-primary hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-muted-foreground">{popis}</span>
      </span>
    </button>
  );
}

// ─── Formulárová vetva pre komponenty bez builder bloku ──────

function KomponentFormDialog({
  zakazkaId,
  variantaId,
  zostavy,
  sloty,
  komponent,
  onBack,
  onClose,
}: {
  zakazkaId: string;
  variantaId: string;
  zostavy: Zostava[];
  sloty: MaterialSlot[];
  komponent: KomponentMeta;
  onBack: () => void;
  onClose: () => void;
}): JSX.Element {
  const kusovnikMut = useKomponentKusovnik();
  const vlozMut = useVlozKomponent(zakazkaId, variantaId);
  const [hodnoty, setHodnoty] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const p of komponent.params) {
      init[p.name] = p.default != null ? String(p.default) : "";
    }
    return init;
  });
  const kod = navrhniKod(zostavy);
  const [nazov, setNazov] = useState(komponent.label);
  const [nahlad, setNahlad] = useState<KomponentKusovnik | null>(null);
  // materiál korpusu (doskové dielce) — default prvý slot s materiálom
  const slotyMat = sloty.filter((s) => s.material);
  const [materialId, setMaterialId] = useState<string>(
    slotyMat[0]?.material?.id ?? "",
  );

  function zostavParams(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const p of komponent.params) {
      const raw = hodnoty[p.name] ?? "";
      if (raw === "") continue; // nechaj default z komponentu
      if (p.typ === "number") out[p.name] = parseFloat(raw.replace(",", "."));
      else if (p.typ === "integer") out[p.name] = parseInt(raw, 10);
      else if (p.typ === "enum") {
        // options môžu byť čísla (počet krídel) aj stringy (typ dverí)
        const opt = (p.options ?? []).find((o) => String(o) === raw);
        out[p.name] = opt ?? raw;
      } else out[p.name] = raw;
    }
    return out;
  }

  async function handleNahlad(): Promise<void> {
    try {
      const res = await kusovnikMut.mutateAsync({
        komponentId: komponent.id,
        params: zostavParams(),
        materialId: materialId || null,
      });
      setNahlad(res.data);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba náhľadu",
        description: extractErrorMessage(err),
      });
    }
  }

  async function handleVloz(): Promise<void> {
    try {
      const res = await vlozMut.mutateAsync({
        komponent_id: komponent.id,
        params: zostavParams(),
        kod,
        nazov: nazov || null,
        pocet_ks: 1,
        material_id: materialId || null,
      });
      const n = res.data.nenajdene.length;
      toast({
        title: `Zostava ${res.data.zostava.kod} vytvorená`,
        description:
          `${res.data.dielce_created} dielcov + ${res.data.kovanie_created} kovanie` +
          (n > 0 ? `; nenájdené v cenníku: ${res.data.nenajdene.join(", ")}` : ""),
        ...(n > 0 ? { variant: "destructive" as const } : {}),
      });
      onClose();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba vloženia",
        description: extractErrorMessage(err),
      });
    }
  }

  function paramInput(p: KomponentParamDef): JSX.Element {
    if (p.typ === "enum") {
      return (
        <Select
          value={hodnoty[p.name] ?? ""}
          onValueChange={(v) => setHodnoty((h) => ({ ...h, [p.name]: v }))}
        >
          <SelectTrigger className="h-8" aria-label={p.label}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(p.options ?? []).map((o) => (
              <SelectItem key={String(o)} value={String(o)}>
                {String(o)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input
        className="h-8"
        inputMode="decimal"
        value={hodnoty[p.name] ?? ""}
        onChange={(e) => setHodnoty((h) => ({ ...h, [p.name]: e.target.value }))}
        aria-label={p.label}
      />
    );
  }

  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{komponent.label}</DialogTitle>
          <DialogDescription className="sr-only">
            Parametre komponentu a kusovník na náhľad pred vložením.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {komponent.popis ? (
            <p className="text-xs text-muted-foreground line-clamp-2">{komponent.popis}</p>
          ) : null}
          <div className="grid grid-cols-3 gap-3">
            {komponent.params.map((p) => (
              <div key={p.name}>
                <Label className="text-xs">
                  {p.label}
                  {p.unit ? ` (${p.unit})` : ""}
                </Label>
                {paramInput(p)}
              </div>
            ))}
          </div>
          <div>
            <Label>Materiál korpusu</Label>
            <Select value={materialId || "auto"} onValueChange={(v) => setMaterialId(v === "auto" ? "" : v)}>
              <SelectTrigger aria-label="Materiál korpusu">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">auto — podľa dekoru komponentu</SelectItem>
                {slotyMat.map((s) => (
                  <SelectItem key={s.id} value={s.material!.id}>
                    {s.slot_kod} {s.material!.nazov}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-0.5 text-[10px] text-muted-foreground">
              Platí pre doskové dielce (boky, plát, dno, police, čelá);
              sololit/HDF sa dohľadá automaticky.
            </p>
          </div>
          {/* Kód zostavy dá appka (KOLO 134) — TAN číslo patrí do názvu */}
          <div>
            <Label>Názov</Label>
            <Input value={nazov} onChange={(e) => setNazov(e.target.value)} />
          </div>

          {nahlad ? (
            <div className="max-h-56 overflow-auto rounded-md border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted">
                  <tr>
                    <th className="p-1.5 text-left">Dielec</th>
                    <th className="p-1.5 text-right">Rozmer</th>
                    <th className="p-1.5 text-right">ks</th>
                    <th className="p-1.5 text-left">Materiál</th>
                  </tr>
                </thead>
                <tbody>
                  {nahlad.dielce.map((d, i) => (
                    <tr key={`d${i}`} className="border-t">
                      <td className="p-1.5">{d.nazov}</td>
                      <td className="p-1.5 text-right font-mono">
                        {d.dlzka_mm}×{d.sirka_mm}×{d.hrubka_mm}
                      </td>
                      <td className="p-1.5 text-right">{d.pocet_ks}</td>
                      <td className={"p-1.5 " + (d.material_nazov ? "" : "text-destructive")}>
                        {d.material_nazov ?? `${d.material_token ?? "—"} (nenájdené)`}
                      </td>
                    </tr>
                  ))}
                  {nahlad.kovanie.map((k, i) => (
                    <tr key={`k${i}`} className="border-t">
                      <td className="p-1.5">kovanie {k.catalog_id}</td>
                      <td className="p-1.5 text-right">—</td>
                      <td className="p-1.5 text-right">{k.pocet_ks}</td>
                      <td className={"p-1.5 " + (k.material_nazov ? "" : "text-destructive")}>
                        {k.material_nazov ?? "nenájdené v cenníku"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onBack}>
            Späť
          </Button>
          <Button
            variant="secondary"
            onClick={handleNahlad}
            disabled={kusovnikMut.isPending}
          >
            {kusovnikMut.isPending ? "Počítam…" : "Náhľad kusovníka"}
          </Button>
          <Button onClick={handleVloz} disabled={vlozMut.isPending}>
            {vlozMut.isPending ? "Vkladám…" : "Vložiť do výpočtu"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
